import React, { useState } from "react";
import { App, Skeleton, Tooltip } from "antd5";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { EllipsisTooltipText } from "lib/core_components/EllipsisTooltip";
import { BidChatResponse_Sources } from "lib/generated/app-service-gql/graphql";
import { BidDocument, useBidDocuments } from "lib/hooks/api/bid/bid_document/useBidDocuments";
import { useDownloadAllBidDocuments } from "lib/hooks/api/bid/bid_document/useDownloadAllBidDocuments";
import { useDownloadBidDocument } from "lib/hooks/api/bid/bid_document/useDownloadBidDocument";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { red500, sysPrimaryDefault, sysTextDefault } from "lib/themes/colors";
import { DeleteBidDocumentModal } from "./ReportModals";
import UploadBidDocumentsModal from "./UploadBidDocumentsModal";

import css from "./DocumentSlideout.module.scss";

type Props = {
  bidId: string;
  opportunityId: string;
  onSelectDocument: (source: BidChatResponse_Sources) => Promise<void>;
  onRegenerateBidReport: () => void;
  hasEditAccess: boolean;
};

export function DocumentSlideout({
  bidId,
  opportunityId,
  onSelectDocument,
  onRegenerateBidReport,
  hasEditAccess,
}: Props) {
  const { message } = App.useApp();
  const { data, isLoading } = useBidDocuments(bidId);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  // TODO: Could possibly enhance to create a zip file and download
  // all documents at once. WOuld need to be a dynamic import using fflate
  const { mutate: downloadAllDocuments } = useDownloadAllBidDocuments({
    onError: (error) => {
      console.error(`Error downloading all documents: ${JSON.stringify(error)}`);
      message.error(`Failed to download all documents`);
    },
  });

  return (
    <div className={css.tenderDocuments}>
      <div className={css.header}>
        <Text h2>Tender documents ({data?.items?.length ?? 0})</Text>

        {hasEditAccess && data && data.items?.length > 0 && (
          <Tooltip title="Download all" placement="bottom">
            <Button
              buttonType="tertiary"
              icon="download01"
              iconProps={{
                size: 20,
                color: sysTextDefault,
              }}
              onClick={() => {
                downloadAllDocuments({
                  documents: data.items.map((doc: BidDocument) => ({
                    documentId: doc.id,
                    fileName: doc.fileName,
                  })),
                  opportunityId,
                });
              }}
            />
          </Tooltip>
        )}
      </div>
      <Flex flexDirection="column" style={{ marginTop: "24px" }} gap={16}>
        {isLoading || !data?.items ? (
          <Skeleton />
        ) : (
          data.items.map((doc: BidDocument) => (
            <DocumentRow
              key={doc.id}
              document={doc}
              onSelectDocument={onSelectDocument}
              bidId={bidId}
              hasEditAccess={hasEditAccess}
            />
          ))
        )}
        {hasEditAccess && (
          <Button
            icon="plus"
            buttonType="secondary"
            iconProps={{ color: sysPrimaryDefault }}
            onClick={() => setIsUploadModalOpen(true)}
          >
            Upload document
          </Button>
        )}
      </Flex>
      <UploadBidDocumentsModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        bidId={bidId}
        onRegenerateBidReport={onRegenerateBidReport}
      />
    </div>
  );
}

function DocumentRow({
  bidId,
  document,
  onSelectDocument,
  hasEditAccess,
}: {
  bidId: string;
  document: BidDocument;
  hasEditAccess: boolean;
  onSelectDocument: (source: BidChatResponse_Sources) => Promise<void>;
}) {
  const { message } = App.useApp();

  const { mutate: downloadDocument } = useDownloadBidDocument({
    onError: (error, vars) => {
      console.error(`Error downloading document: ${JSON.stringify(error)}`);
      message.error(`Failed to download document ${vars.fileName}`);
    },
  });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  return (
    <div
      key={document.id}
      className={css.item}
      onClick={async () =>
        await onSelectDocument({
          documentId: document.id,
          document: document.fileName,
          locations: [],
        })
      }
    >
      <span className={css.content}>
        <UIcon icon="fileAttachment" className={css.icon} />
        <EllipsisTooltipText fullText={document.fileName} containerClassname={css.documentText} />
      </span>
      {hasEditAccess && (
        <span className={css.actionItems}>
          <Button
            buttonType="tertiary"
            icon="download01"
            className={css.actionBtn}
            iconProps={{
              size: 16,
              color: sysTextDefault,
            }}
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              e.preventDefault();

              downloadDocument({ documentId: document.id, fileName: document.fileName });
            }}
          />
          <Button
            buttonType="tertiary"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              e.preventDefault();
              setDeleteModalOpen(true);
            }}
            className={css.deleteButton}
            icon="trash03"
            iconProps={{ color: red500 }}
          />
        </span>
      )}
      <DeleteBidDocumentModal
        bidId={bidId}
        documentId={document.id}
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      />
    </div>
  );
}
